import axios from "axios";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ApiUrl from "./../Environment/ApiUrl";
import { useToast } from "../Toast/ToastContext";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

function Personal_Stock() {
  const toast = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("day-wise");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAllItems, setShowAllItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedDate, setSelectedDate] = useState({}); // Default date
  const [exchangeModal, setExchangeModal] = useState(false);
  const [exchangeItem, setExchangeItem] = useState(null);
  const [exchangeWeight, setExchangeWeight] = useState("");
  const [exchangeQty, setExchangeQty] = useState("");
  const [exchangeLoading, setExchangeLoading] = useState(false);

  // Handle tab click
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm("");
  };

  // Navigate to add stock pages
  const handleAddStock = () => {
    navigate("/add-personal-stock");
  };

  const handleAddPersonalStock = () => {
    navigate("/stocks");
  };

  // Fetch data based on active tab
  useEffect(() => {
    fetchData();
  }, [activeTab, selectedDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      let url;
      if (activeTab === "day-wise") {
        url = `${ApiUrl}/Api/Personal_stock.php`;
      } else {
        url = `${ApiUrl}/Api/Personal_stock.php`;
      }

      const response = await axios.get(url);
      console.log("Response from stock:", response.data?.data);

      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      setResponseData(data);

      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Error fetching data:", error.message);
      toast.error("Failed to fetch data", 3000, "topRight");
      setResponseData([]);
      setLoading(false);
    }
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!Array.isArray(responseData)) return [];

    return responseData.filter((item) => {
      if (!item) return false;

      const searchMatch =
        searchTerm === "" ||
        (item.item &&
          item.item.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.category &&
          item.category.toLowerCase().includes(searchTerm.toLowerCase()));

      return searchMatch;
    });
  }, [responseData, searchTerm]);

  // Calculate pagination values
  const totalItems = filteredData.length;
  const totalPages = showAllItems ? 1 : Math.ceil(totalItems / itemsPerPage);

  // Get current items for pagination
  const currentItems = showAllItems
    ? filteredData
    : filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
      );

  // Pagination handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === "All") {
      setShowAllItems(true);
      setCurrentPage(1);
    } else {
      setShowAllItems(false);
      setItemsPerPage(Number(value));
      setCurrentPage(1);
    }
  };

  // Date picker change handler
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // Export Functions
  const handlePrint = () => {
    let tableHtml = "";
    let title = "";

    if (activeTab === "day-wise") {
      title = `Day-Wise Stock Report (${selectedDate})`;
      tableHtml = `
        <h2>${title}</h2>
        <table style="width:100%; border-collapse: collapse;">
          <thead>
            <tr style="background-color: #f2f2f2;">
              <th style="border: 1px solid #ddd; padding: 8px;">S.No</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Category</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Item</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Weight</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Total Quantity</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Sold Quantity</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Available Quantity</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Stock IDs</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData
              .map(
                (item, index) => `
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  index + 1
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item?.category || ""
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.item || ""
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.weight || ""
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.total_quantity || ""
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.sold_quantity || ""
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.avaliable_quantity || ""
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.stock_ids && item.stock_ids.length > 0
                    ? item.stock_ids.join(", ")
                    : "No IDs"
                }</td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
      `;
    } else {
      title = "Stock Inventory Report";
      tableHtml = `
        <h2>${title}</h2>
        <table style="width:100%; border-collapse: collapse;">
          <thead>
            <tr style="background-color: #f2f2f2;">
              <th style="border: 1px solid #ddd; padding: 8px;">S.No</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Category</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Type</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Weight</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Total</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Stock IDs</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData
              .map(
                (item, index) => `
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  index + 1
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.category || "N/A"
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.item_name_tamil
                    ? `${item.item_name_english || item.item || item.product} / ${item.item_name_tamil}`
                    : item.item_name_english ||
                      item.item ||
                      item.product ||
                      "N/A"
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.weight || "N/A"
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.total || item.total_quantity || "N/A"
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.stock_ids && item.stock_ids.length > 0
                    ? item.stock_ids.join(", ")
                    : "No IDs"
                }</td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
      `;
    }

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h2 { color: #333; text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            @media print {
              body { margin: 0; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; page-break-after: auto; }
            }
          </style>
        </head>
        <body>
          ${tableHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();

      if (activeTab === "day-wise") {
        doc.text(`Day-Wise Stock Report (${selectedDate})`, 14, 10);

        autoTable(doc, {
          head: [
            [
              "S.No",
              "Category",
              "Item",
              "Weight",
              "Total Qty",
              "Sold Qty",
              "Available Qty",
              "Stock IDs",
            ],
          ],
          body: filteredData.map((item, index) => [
            index + 1,
            item?.category || "",
            item.item || "",
            item.weight || "",
            item.total_quantity || "",
            item.sold_quantity || "",
            item.avaliable_quantity || "",
            item.stock_ids && item.stock_ids.length > 0
              ? item.stock_ids.join(", ")
              : "No IDs",
          ]),
          startY: 20,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [66, 139, 202] },
        });
        doc.save(`day_wise_stock_report_${selectedDate}.pdf`);
      } else {
        doc.text("Stock Inventory Report", 14, 10);

        autoTable(doc, {
          head: [
            ["S.No", "Category", "Item Name", "Weight", "Total", "Stock IDs"],
          ],
          body: filteredData.map((item, index) => [
            index + 1,
            item.category || "N/A",
            item.item_name_tamil
              ? `${item.item || item.type} / ${item.item_name_tamil}`
              : item.item || item.type || "N/A",
            item.weight || "N/A",
            item.total || item.total_quantity || "N/A",
            item.stock_ids && item.stock_ids.length > 0
              ? item.stock_ids.join(", ")
              : "No IDs",
          ]),
          startY: 20,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [66, 139, 202] },
        });
        doc.save("stock_inventory_report.pdf");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF", 3000, "topRight");
    }
  };

  const exportCSV = () => {
    let headers;
    let rows;
    let filename;

    if (activeTab === "day-wise") {
      headers = [
        "S.No",
        "Category",
        "Item",
        "Weight",
        "Total Quantity",
        "Sold Quantity",
        "Available Quantity",
        "Stock IDs",
      ];
      rows = filteredData.map((item, index) => [
        index + 1,
        item?.category || "",
        item.item || "",
        item.weight || "",
        item.total_quantity || "",
        item.sold_quantity || "",
        item.avaliable_quantity || "",
        item.stock_ids && item.stock_ids.length > 0
          ? item.stock_ids.join("; ")
          : "No IDs",
      ]);
      filename = `day_wise_stock_report_${selectedDate}.csv`;
    } else {
      headers = [
        "S.No",
        "Category",
        "Item Name",
        "Weight",
        "Total",
        "Stock IDs",
      ];
      rows = filteredData.map((item, index) => [
        index + 1,
        item.category || "N/A",
        item.item_name_tamil
          ? `${item.item || item.type} / ${item.item_name_tamil}`
          : item.item || item.type || "N/A",
        item.weight || "N/A",
        item.total || item.total_quantity || "N/A",
        item.stock_ids && item.stock_ids.length > 0
          ? item.stock_ids.join("; ")
          : "No IDs",
      ]);
      filename = "stock_inventory_report.csv";
    }

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((field) => `"${field}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    let wsData;
    let filename;

    if (activeTab === "day-wise") {
      wsData = [
        [
          "S.No",
          "Category",
          "Item",
          "Weight",
          "Total Quantity",
          "Sold Quantity",
          "Available Quantity",
          "Stock IDs",
        ],
        ...filteredData.map((item, index) => [
          index + 1,
          item?.category || "",
          item.item || "",
          item.weight || "",
          item.total_quantity || "",
          item.sold_quantity || "",
          item.avaliable_quantity || "",
          item.stock_ids && item.stock_ids.length > 0
            ? item.stock_ids.join("; ")
            : "No IDs",
        ]),
      ];
      filename = `day_wise_stock_report_${selectedDate}.xlsx`;
    } else {
      wsData = [
        ["S.No", "Category", "Item Name", "Weight", "Total", "Stock IDs"],
        ...filteredData.map((item, index) => [
          index + 1,
          item.category || "N/A",
          item.item_name_tamil
            ? `${item.item || item.type} / ${item.item_name_tamil}`
            : item.item || item.type || "N/A",
          item.weight || "N/A",
          item.total || item.total_quantity || "N/A",
          item.stock_ids && item.stock_ids.length > 0
            ? item.stock_ids.join("; ")
            : "No IDs",
        ]),
      ];
      filename = "stock_inventory_report.xlsx";
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock Report");

    // Auto-size columns
    const colWidths = wsData[0].map((_, colIndex) => {
      const maxLength = Math.max(
        ...wsData.map((row) =>
          row[colIndex] ? row[colIndex].toString().length : 0,
        ),
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    ws["!cols"] = colWidths;

    XLSX.writeFile(wb, filename);
  };

  const copyToClipboard = () => {
    let headers;
    let rows;

    if (activeTab === "day-wise") {
      headers = [
        "S.No",
        "Category",
        "Item",
        "Weight",
        "Total Quantity",
        "Sold Quantity",
        "Available Quantity",
        "Stock IDs",
      ];
      rows = filteredData.map((item, index) => [
        index + 1,
        item?.category || "",
        item.item || "",
        item.weight || "",
        item.total_quantity || "",
        item.sold_quantity || "",
        item.avaliable_quantity || "",
        item.stock_ids && item.stock_ids.length > 0
          ? item.stock_ids.join("; ")
          : "No IDs",
      ]);
    } else {
      headers = [
        "S.No",
        "Category",
        "Item Name",
        "Weight",
        "Total",
        "Stock IDs",
      ];
      rows = filteredData.map((item, index) => [
        index + 1,
        item.category || "N/A",
        item.item_name_tamil
          ? `${item.item || item.type} / ${item.item_name_tamil}`
          : item.item || item.type || "N/A",
        item.weight || "N/A",
        item.total || item.total_quantity || "N/A",
        item.stock_ids && item.stock_ids.length > 0
          ? item.stock_ids.join("; ")
          : "No IDs",
      ]);
    }

    const formattedText = [headers, ...rows]
      .map((row) => row.map((field) => `"${field}"`).join("\t"))
      .join("\n");

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(formattedText)
        .then(() => {
          toast.success("Table data copied to clipboard!", 3000, "topRight");
        })
        .catch((err) => {
          console.error("Clipboard API failed:", err);
          fallbackCopy(formattedText);
        });
    } else {
      fallbackCopy(formattedText);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.top = 0;
      textArea.style.left = 0;
      textArea.style.width = "2em";
      textArea.style.height = "2em";
      textArea.style.padding = 0;
      textArea.style.border = "none";
      textArea.style.outline = "none";
      textArea.style.boxShadow = "none";
      textArea.style.background = "transparent";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        toast.success("Table data copied to clipboard!", 3000, "topRight");
      } else {
        toast.error("Failed to copy data", 3000, "topRight");
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error("Failed to copy data", 3000, "topRight");
    }
  };

  // Render pagination buttons
  const renderPageNumbers = () => {
    if (showAllItems || totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <li
          key={1}
          className={`page-item ${currentPage === 1 ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>,
      );
      if (startPage > 2) {
        pages.push(
          <li key="start-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="end-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>,
        );
      }
      pages.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>,
      );
    }

    return pages;
  };

  const userRole = localStorage.getItem("role");

  const handleEditstock = (item) => {
    const stockId = item.id || item.stock_ids?.[0]; // Use item.id or first stock_id

    // Create stock_ids string for the route
    const stockIdsString = Array.isArray(item.stock_ids)
      ? item.stock_ids.join(",")
      : item.stock_ids;

    // if (userRole === "Employee") {
    if (userRole === "Employee" || userRole === "Manager") {
      navigate(`/Edit-personalstock/${stockIdsString}`);
    } else {
      navigate(`/Edit-personalstock/${stockIdsString}`);
    }
  };

  const handleEdit = (item) => {
    console.log("Edit item:", item);
    const ids = item.stock_ids.join(",");
    navigate(`/PersonalviewStock/${ids}`);
  };

  // Show delete confirmation modal
  const handleDelete = (item) => {
    console.log("Delete item:", item);
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  // Exchange: open modal
  const handleExchange = (item) => {
    setExchangeItem(item);
    setExchangeWeight("");
    setExchangeQty("");
    setExchangeModal(true);
  };

  // Exchange: confirm - reduce from personal stock, add to main stock
  const handleExchangeConfirm = async () => {
    const hasQtyInput = exchangeQty !== "" && exchangeQty !== null && exchangeQty !== undefined;
    const hasWeightInput = exchangeWeight !== "" && exchangeWeight !== null && exchangeWeight !== undefined;

    if (!hasQtyInput && !hasWeightInput) {
      toast.error("Please enter quantity or weight to exchange", 3000, "topRight");
      return;
    }

    const availableQty = parseInt(exchangeItem?.avaliable_quantity) || 0;

    // Per-qty (average) weight of the personal stock item, used to derive
    // whichever of qty/weight wasn't entered.
    const totalWeightNum =
      parseFloat(String(exchangeItem?.weight || "").replace(/[^0-9.]/g, "")) ||
      0;
    const availQtyForCalc =
      parseInt(exchangeItem?.avaliable_quantity) ||
      parseInt(exchangeItem?.total_quantity) ||
      0;
    const perQtyWeight =
      totalWeightNum > 0 && availQtyForCalc > 0
        ? totalWeightNum / availQtyForCalc
        : parseFloat(exchangeItem?.per_qty_weight || 0);

    let qty = hasQtyInput ? parseInt(exchangeQty) || 0 : 0;
    let weight = hasWeightInput ? parseFloat(exchangeWeight) || 0 : 0;

    if (hasQtyInput && !hasWeightInput) {
      // Only qty given — derive weight from the per-qty ratio.
      weight = perQtyWeight > 0 ? parseFloat((qty * perQtyWeight).toFixed(3)) : 0;
      if (weight <= 0) {
        toast.error(
          "Unable to calculate weight for that quantity. Please enter weight manually.",
          3000,
          "topRight",
        );
        return;
      }
    } else if (hasWeightInput && !hasQtyInput) {
      // Only weight given — derive qty from the per-qty ratio (rounded).
      qty = perQtyWeight > 0 ? Math.round(weight / perQtyWeight) : 0;
    }

    if (hasQtyInput && qty <= 0) {
      toast.error("Quantity must be greater than 0", 3000, "topRight");
      return;
    }

    if (weight <= 0 && !hasWeightInput) {
      toast.error(
        "Unable to determine weight to exchange. Please enter weight manually.",
        3000,
        "topRight",
      );
      return;
    }

    if (qty > availableQty) {
      toast.error(
        "Exchange quantity exceeds available quantity",
        3000,
        "topRight",
      );
      return;
    }

    if (weight <= 0) {
      toast.error(
        "Unable to calculate weight. Please enter weight manually.",
        3000,
        "topRight",
      );
      return;
    }

    const stockId = exchangeItem?.stock_ids?.[0] || exchangeItem?.id;
    if (!stockId) {
      toast.error(
        "Stock ID not found. Please refresh and try again.",
        3000,
        "topRight",
      );
      return;
    }

    try {
      setExchangeLoading(true);
      const today = new Date().toISOString().split("T")[0];
      const createdBy = localStorage.getItem("username") || "system";

      // IMPORTANT: Do NOT include any 'id' fields
      const stockPayload = {
        item: exchangeItem.item,
        category: exchangeItem.category,
        quantity: qty,
        amount: 0,
        weight: weight.toFixed(3) + "g",
        avaliable_quantity: qty,
        total_quantity: qty,
        sold_quantity: 0,
        created_by: createdBy,
        created_date: today,
        stock_list: [
          {
            date: today,
            status: "In",
            item: exchangeItem.item,
            quantity: qty,
            category: exchangeItem.category,
            weight: weight.toFixed(3) + "g",
            created_by: createdBy,
            created_date: today,
            // NO 'id' field here - database will auto-generate
          },
        ],
      };

      console.log(
        "Sending exchange payload:",
        JSON.stringify(stockPayload, null, 2),
      );

      // Add to main stock
      await axios.post(`${ApiUrl}/Api/stock.php`, stockPayload, {
        headers: { "Content-Type": "application/json" },
      });

      // Reduce personal stock
      const updatePayload = {
        exchange_weight: weight,
        exchange_quantity: qty,
        action: "exchange",
      };

      await axios.put(
        `${ApiUrl}/Api/Personal_stock.php/${stockId}`,
        updatePayload,
        { headers: { "Content-Type": "application/json" } },
      );

      toast.success(
        "Exchange successful! Items moved to main stock.",
        3000,
        "topRight",
      );
      setExchangeModal(false);
      fetchData(); // Refresh personal stock data
    } catch (error) {
      console.error("Exchange error:", error);
      console.error("Error response:", error.response?.data);
      toast.error(
        "Exchange failed: " + (error.response?.data?.message || error.message),
        3000,
        "topRight",
      );
    } finally {
      setExchangeLoading(false);
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  // Delete confirmation
  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setDeleteLoading(true);

      const stockIdsToDelete = itemToDelete.stock_ids || [];

      if (stockIdsToDelete.length === 0) {
        throw new Error("No stock IDs found to delete");
      }

      const deleteData = {
        stock_ids: stockIdsToDelete,
        item_name: itemToDelete.item,
        category: itemToDelete.category,
      };

      console.log("Deleting stock with data:", deleteData);

      const response = await axios.delete(`${ApiUrl}/Api/personal_stock.php`, {
        data: deleteData,
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Delete response:", response.data);

      if (response.data.status === true || response.data.success === true) {
        setResponseData((prevData) =>
          prevData.filter((item) => {
            if (!item.stock_ids || !Array.isArray(item.stock_ids)) return true;
            const hasOverlap = item.stock_ids.some((stockId) =>
              stockIdsToDelete.includes(stockId),
            );
            return !hasOverlap;
          }),
        );

        toast.success(
          `Stock item "${itemToDelete.item}" deleted successfully`,
          3000,
          "topRight",
        );

        closeDeleteModal();

        const remainingItems = responseData.filter((item) => {
          if (!item.stock_ids || !Array.isArray(item.stock_ids)) return true;
          const hasOverlap = item.stock_ids.some((stockId) =>
            stockIdsToDelete.includes(stockId),
          );
          return !hasOverlap;
        });

        if (!showAllItems) {
          const newTotalPages = Math.ceil(remainingItems.length / itemsPerPage);
          if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
          }
        }

        if (response.data.warnings && response.data.warnings.length > 0) {
          console.warn("Delete warnings:", response.data.warnings);
          toast.warning(
            `Some issues occurred: ${response.data.warnings.join(", ")}`,
            5000,
            "topRight",
          );
        }
      } else {
        throw new Error(response.data.message || "Delete operation failed");
      }
    } catch (error) {
      console.error("Error deleting stock item:", error);
      let errorMessage = "Failed to delete stock item";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, 5000, "topRight");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Delete Modal Component
  const DeleteModal = () => {
    if (!showDeleteModal || !itemToDelete) return null;

    return (
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Confirm Delete Stock Item
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={closeDeleteModal}
                disabled={deleteLoading}
              ></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning d-flex align-items-center mb-4">
                <i
                  className="fas fa-exclamation-triangle me-3"
                  style={{ fontSize: "2rem" }}
                ></i>
                <div>
                  <h6 className="mb-1">
                    Warning: This action cannot be undone!
                  </h6>
                  <p className="mb-0">
                    You are about to permanently delete this stock item and all
                    its associated records.
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="card border-primary h-100">
                    <div className="card-header bg-primary text-white">
                      <h6 className="mb-0">
                        <i className="fas fa-info-circle me-2"></i>
                        Item Summary
                      </h6>
                    </div>
                    <div className="card-body">
                      <table className="table table-borderless table-sm">
                        <tbody>
                          <tr>
                            <td>
                              <strong>Item Name:</strong>
                            </td>
                            <td>{itemToDelete.item}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Category:</strong>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  itemToDelete.category === "Gold"
                                    ? "bg-warning text-dark"
                                    : "bg-secondary"
                                }`}
                              >
                                {itemToDelete.category}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Weight:</strong>
                            </td>
                            <td>{itemToDelete.weight}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Total Quantity:</strong>
                            </td>
                            <td>
                              <span className="badge bg-info">
                                {itemToDelete.total_quantity}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Sold Quantity:</strong>
                            </td>
                            <td>
                              <span className="badge bg-success">
                                {itemToDelete.sold_quantity}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Available Quantity:</strong>
                            </td>
                            <td>
                              <span className="badge bg-primary">
                                {itemToDelete.avaliable_quantity}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card border-warning h-100">
                    <div className="card-header bg-warning text-dark">
                      <h6 className="mb-0">
                        <i className="fas fa-database me-2"></i>
                        Records to Delete
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <strong>Stock IDs:</strong>
                        <div className="mt-2">
                          {itemToDelete.stock_ids &&
                            itemToDelete.stock_ids.map((stockId, index) => (
                              <span
                                key={index}
                                className="badge bg-danger me-1 mb-1"
                              >
                                ID: {stockId}
                              </span>
                            ))}
                        </div>
                      </div>

                      {itemToDelete.stock_list &&
                        itemToDelete.stock_list.length > 0 && (
                          <>
                            <strong>
                              Stock Records ({itemToDelete.stock_list.length}{" "}
                              total):
                            </strong>
                            <div
                              className="mt-2"
                              style={{ maxHeight: "200px", overflowY: "auto" }}
                            >
                              {itemToDelete.stock_list.map((stock, index) => (
                                <div
                                  key={index}
                                  className="border rounded p-2 mb-2 bg-light"
                                >
                                  <div className="row">
                                    <div className="col-6">
                                      <small>
                                        <strong>Record ID:</strong> {stock.id}
                                        <br />
                                        <strong>Date:</strong> {stock.date}
                                        <br />
                                        <strong>Stock ID:</strong>{" "}
                                        {stock.overall_stock_id}
                                      </small>
                                    </div>
                                    <div className="col-6">
                                      <small>
                                        <strong>Quantity:</strong>{" "}
                                        {stock.quantity}
                                        <br />
                                        <strong>Status:</strong>
                                        <span
                                          className={`badge ms-1 ${
                                            stock.status === "in"
                                              ? "bg-success"
                                              : "bg-warning text-dark"
                                          }`}
                                        >
                                          {stock.status}
                                        </span>
                                        <br />
                                        <strong>Available:</strong>{" "}
                                        {stock.available_quantity}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="alert alert-danger mt-3 mb-0">
                <strong>Impact:</strong> Deleting this item will remove{" "}
                <strong>{itemToDelete.stock_list?.length || 0}</strong> database
                records and make{" "}
                <strong>{itemToDelete.avaliable_quantity}</strong> units
                unavailable.
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
                disabled={deleteLoading}
              >
                <i className="fas fa-times me-2"></i>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash me-2"></i>
                    Permanently Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid py-3">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-message">
            <div className="loadinggif"></div>
          </div>
        </div>
      )}

      <>
        <div className="row mt-2">
          <div className="col-lg-2">
            <button
              className="btn btn-primary btn-sm rounded-3"
              style={{ marginTop: "-30px" }}
              onClick={handleAddStock}
            >
              Add Personal Stock <i className="fa-solid fa-plus mx-1"></i>
            </button>
          </div>
          <div className="col-lg-8"></div>
          <div className="col-lg-2">
            <button
              className="btn btn-success btn-sm rounded-3"
              style={{ marginTop: "-30px" }}
              onClick={handleAddPersonalStock}
            >
              <i className="fa-solid fa-arrow-left mx-1"></i>
              Back Stock
            </button>
          </div>
        </div>
        <div className="">
          {/* Tabs */}
          <div className="card shadow-sm border-0 rounded-3 mb-2">
            <div className="row p-2">
              <div className="col-auto">
                <button
                  className={`btn btn-link text-decoration-none px-3 ${
                    activeTab === "day-wise"
                      ? "text-white border-bottom bg-primary"
                      : "text-dark"
                  }`}
                  onClick={() => handleTabClick("day-wise")}
                >
                  <span style={{ fontSize: "13px" }}>Day-Wise</span>
                </button>
                <button
                  className={`btn btn-link text-decoration-none px-3 ms-3 ${
                    activeTab === "stock-inventory"
                      ? "text-white border-bottom bg-primary"
                      : "text-dark"
                  }`}
                  onClick={() => handleTabClick("stock-inventory")}
                >
                  <span style={{ fontSize: "13px" }}>Stock Inventory</span>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {activeTab === "day-wise" && (
            <>
              <div className="row mb-2">
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Total Buy Qty</h6>
                    <p className="fw-bold">1,258,45 grams</p>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Total Sales Qty</h6>
                    <p className="fw-bold">1,258,45 grams</p>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Available Qty</h6>
                    <p className="fw-bold">346</p>
                  </div>
                </div>
              </div>
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center mb-2 mb-md-0">
                      <label className="form-label text-dark mb-0 me-2">
                        Show:{" "}
                      </label>
                      <div>
                        <select
                          className="form-control"
                          onChange={handleItemsPerPageChange}
                          value={showAllItems ? "All" : itemsPerPage}
                        >
                          <option value="All">All</option>
                          {[5, 10, 20, 100, 200, 500, 1000].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                              {pageSize}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="d-flex justify-content-center mb-2 mb-md-0 flex-wrap">
                      <button
                        className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0"
                        onClick={handlePrint}
                      >
                        Print
                      </button>
                      <button
                        className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0"
                        onClick={exportPDF}
                      >
                        PDF
                      </button>
                      <button
                        className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0"
                        onClick={copyToClipboard}
                      >
                        Copy
                      </button>
                      <button
                        className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0"
                        onClick={exportCSV}
                      >
                        CSV
                      </button>
                      <button
                        className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0"
                        onClick={exportExcel}
                      >
                        Excel
                      </button>
                    </div>

                    <div className="search-container w-25">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search"
                          aria-label="Search"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="card tableCard p-2">
                    <div className="table-responsive">
                      <table className="table table-hover w-100">
                        <thead className="bg-light">
                          <tr>
                            <th>S.No</th>
                            <th>Date</th>
                            <th>Category</th>
                            <th>Item</th>
                            <th>Total Weight</th>
                            <th>Sold Weight</th>
                            <th>Avail. Weight</th>
                            <th>Total Qty</th>
                            <th>Sold Qty</th>
                            <th>Exchanged Qty</th>
                            <th>Avail. Qty</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.length > 0 ? (
                            currentItems.map((item, index) => (
                              <tr key={item.id || `inventory-${index}`}>
                                <td>
                                  {showAllItems
                                    ? index + 1
                                    : (currentPage - 1) * itemsPerPage +
                                      index +
                                      1}
                                </td>
                                <td>
                                  {item?.personal_stock_list &&
                                  Array.isArray(item.personal_stock_list) &&
                                  item.personal_stock_list.length > 0
                                    ? item.personal_stock_list[0]?.date || "N/A"
                                    : "N/A"}
                                </td>
                                <td>{item?.category || "N/A"}</td>
                                <td>
                                  {item?.item_name_tamil
                                    ? `${item?.item || ""} / ${item.item_name_tamil}`
                                    : item?.item || "N/A"}
                                </td>
                                <td>{item?.weight || "N/A"}</td>
                                <td>{item?.sold_weight || "0"}</td>
                                <td>
                                  {(() => {
                                    const total = parseFloat(item?.weight) || 0;
                                    const sold =
                                      parseFloat(item?.sold_weight) || 0;
                                    return (total - sold).toFixed(3);
                                  })()}
                                </td>
                                <td>{item?.total_quantity || "N/A"}</td>
                                <td>{item?.sold_quantity || "0"}</td>
                                <td>
                                  <span
                                    className={
                                      item?.exchanged_quantity > 0
                                        ? "badge bg-secondary"
                                        : ""
                                    }
                                  >
                                    {item?.exchanged_quantity || "0"}
                                  </span>
                                </td>
                                <td>{item?.avaliable_quantity || "N/A"}</td>
                                <td>
                                  <button
                                    className="btn btn-light btn-sm mx-1"
                                    onClick={() => handleEditstock(item)}
                                    title="Edit"
                                  >
                                    <i className="fa-solid fa-pen text-primary fs-6"></i>
                                  </button>
                                  <button
                                    className="btn btn-warning btn-sm mx-1"
                                    onClick={() => handleExchange(item)}
                                    title="Exchange to Main Stock"
                                  >
                                    <i className="bi bi-arrow-left-right"></i>
                                  </button>
                                  <button
                                    className="btn btn-light btn-sm"
                                    onClick={() => handleDelete(item)}
                                    title="Delete"
                                  >
                                    <i className="fa-solid fa-trash text-danger fs-6"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="12" className="text-center">
                                No data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {!showAllItems && totalPages > 1 && (
                      <nav aria-label="Page navigation">
                        <ul className="pagination justify-content-end mt-3">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {renderPageNumbers()}
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "stock-inventory" && (
            <>
              <div className="row mb-2">
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Total Gold Stock</h6>
                    <p className="fw-bold text-warning">1,258,45 grams</p>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Total Silver Stock</h6>
                    <p className="fw-bold text-secondary">1,258,45 grams</p>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Total Items</h6>
                    <p className="fw-bold">346</p>
                  </div>
                </div>
                <div className="col-lg-3"></div>
              </div>
              <div
                className="card shadow-sm border-0 rounded-3"
                style={{ background: "#ffffff", opacity: 0.9 }}
              >
                <div className="card-body">
                  <div className="row align-items-center mb-4">
                    <div className="col-md-9">
                      <h6 className="mb-0">Stock Inventory</h6>
                    </div>
                    <div className="col-md-3">
                      <div className="position-relative">
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                        <input
                          type="search"
                          className="form-control ps-5"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="card tableCard p-2">
                    <div className="table-responsive">
                      <table className="table table-hover w-100">
                        <thead className="bg-light">
                          <tr>
                            <th>S.No</th>
                            <th>Category</th>
                            <th>Item Name</th>
                            <th>Weight</th>
                            <th>Total Quantity</th>
                            <th>Sold Quantity</th>
                            <th>Available Quantity</th>

                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.length > 0 ? (
                            currentItems.map((item, index) => (
                              <tr key={item.id || `inventory-${index}`}>
                                <td>
                                  {showAllItems
                                    ? index + 1
                                    : (currentPage - 1) * itemsPerPage +
                                      index +
                                      1}
                                </td>
                                <td>{item?.category || "N/A"}</td>
                                <td>
                                  {item?.item_name_tamil
                                    ? `${item?.item || item?.type || ""} / ${item.item_name_tamil}`
                                    : item?.item || item?.type || "N/A"}
                                </td>
                                <td>{item?.weight || "N/A"}</td>
                                <td>{item?.total_quantity || "N/A"}</td>
                                <td>{item?.sold_quantity || "0"}</td>
                                <td>{item?.avaliable_quantity || "N/A"}</td>
                                <td>
                                  <button
                                    className="btn btn-light btn-sm mx-1"
                                    onClick={() => handleEdit(item)}
                                    title="Edit"
                                  >
                                    <i className="fa-solid fa-list text-primary fs-6"></i>
                                  </button>
                                  <button
                                    className="btn btn-light btn-sm"
                                    onClick={() => handleDelete(item)}
                                    title="Delete"
                                  >
                                    <i className="fa-solid fa-trash text-danger fs-6"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center">
                                No data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {!showAllItems && totalPages > 1 && (
                      <nav aria-label="Page navigation">
                        <ul className="pagination justify-content-end mt-3">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {renderPageNumbers()}
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </>

      {/* Delete Confirmation Modal */}
      <DeleteModal />

      {/* Exchange Modal */}
      {exchangeModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title text-primary">
                  Exchange to Main Stock
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={() => setExchangeModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="alert alert-info py-2 small mb-3">
                  <strong>Item:</strong> {exchangeItem?.item} &nbsp;|&nbsp;
                  <strong>Category:</strong> {exchangeItem?.category}{" "}
                  &nbsp;|&nbsp;
                  <strong>Available Qty:</strong>{" "}
                  {exchangeItem?.avaliable_quantity} &nbsp;|&nbsp;
                  <strong>Total Weight:</strong> {exchangeItem?.weight}{" "}
                  &nbsp;|&nbsp;
                  <strong>Per Qty Weight:</strong>{" "}
                  {exchangeItem?.avaliable_quantity > 0
                    ? (
                        parseFloat(
                          String(exchangeItem?.weight || "").replace(
                            /[^0-9.]/g,
                            "",
                          ),
                        ) / parseInt(exchangeItem.avaliable_quantity)
                      ).toFixed(3)
                    : "N/A"}
                  g
                </div>
                {(() => {
                  const totalW =
                    parseFloat(
                      String(exchangeItem?.weight || "").replace(
                        /[^0-9.]/g,
                        "",
                      ),
                    ) || 0;
                  const availQ =
                    parseInt(exchangeItem?.avaliable_quantity) ||
                    parseInt(exchangeItem?.total_quantity) ||
                    1;
                  const perQW = totalW > 0 ? totalW / availQ : 0;
                  const autoWeight =
                    exchangeQty && perQW > 0
                      ? (parseInt(exchangeQty) * perQW).toFixed(3)
                      : "";
                  return (
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label">
                          Quantity to Exchange
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter quantity"
                          value={exchangeQty}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*$/.test(val)) {
                              setExchangeQty(val);
                              // Keep weight in sync only while the user
                              // hasn't typed a weight of their own.
                              if (!exchangeWeight && perQW > 0 && val) {
                                setExchangeWeight(
                                  (parseInt(val) * perQW).toFixed(3),
                                );
                              }
                            }
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          Weight to Exchange (g)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={
                            perQW > 0 && autoWeight
                              ? `Auto: ${autoWeight}g`
                              : "Enter weight"
                          }
                          value={exchangeWeight}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*\.?\d{0,3}$/.test(val) || val === "") {
                              setExchangeWeight(val);
                            }
                          }}
                        />
                        {perQW > 0 && (
                          <small className="text-muted">
                            ~{perQW.toFixed(3)}g per piece. Enter qty or
                            weight — the other is filled in automatically.
                          </small>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setExchangeModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-warning btn-sm"
                  onClick={handleExchangeConfirm}
                  disabled={exchangeLoading}
                >
                  {exchangeLoading ? "Processing..." : "OK - Exchange"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Personal_Stock;